import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSSE, type SSEEvent } from './useSSE'
import { queryKeys } from '@/lib/queryKeys'
import { useNotificationStore } from '@/stores/useNotificationStore'
import type { NotificationType } from '@/types/notification'

/**
 * Invalidate relevant query caches based on the notification type so that
 * pages the user is currently viewing reflect the change in real time.
 *
 * React Query only refetches invalidated queries that have active observers,
 * so broad invalidation here is safe — no unnecessary network requests.
 */
function invalidateForNotificationType(
  queryClient: ReturnType<typeof useQueryClient>,
  type: NotificationType,
  data: Record<string, unknown> | null | undefined
) {
  const planId =
    (data?.plan_id as string | undefined) ??
    (data?.entity_type === 'plan'
      ? (data?.entity_id as string | undefined)
      : undefined)
  const versionId = data?.version_id as string | undefined

  switch (type) {
    case 'COMMENT_ADDED': {
      const entityType = data?.entity_type as string | undefined
      const entityId = data?.entity_id as string | undefined
      const nestedData = data?.data as Record<string, unknown> | undefined
      const lineRefId =
        (data?.line_ref_id as string | undefined) ??
        (nestedData?.line_ref_id as string | undefined) ??
        (entityType === 'line_item' ? entityId : undefined)

      if (entityType === 'line_item' && lineRefId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.lineItemComments.byRefId(lineRefId),
        })
        if (planId) {
          queryClient.invalidateQueries({
            queryKey: ['dag-table', 'rows', planId],
          })
        }
      } else if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.comments.byPlanId(planId),
        })
      }
      break
    }

    case 'APPROVAL_DECISION':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.submissions.byPlanId(planId),
        })
      }
      break

    case 'OWNERSHIP_TRANSFERRED':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      break

    case 'APPROVAL_REQUIRED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.manager.all,
      })
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
      }
      break

    case 'SOURCING_OPTION_ADDED':
      if (planId && versionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
        })
      }
      break

    case 'PLAN_UNSUBMITTED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.manager.all,
      })
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.submissions.byPlanId(planId),
        })
      }
      break

    case 'PLAN_UNAPPROVED':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.submissions.byPlanId(planId),
        })
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      break

    case 'PLAN_SHARED':
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      break

    case 'MILESTONE_APPROACHING':
    case 'MILESTONE_OVERDUE':
      if (planId) {
        queryClient.invalidateQueries({
          queryKey: ['plan-detail', 'calendar', planId],
        })
      }
      break

    // SOURCING_REQUEST_ASSIGNED: no additional invalidation needed
  }
}

export function useNotificationSSE() {
  const queryClient = useQueryClient()
  const incrementUnreadCount = useNotificationStore(s => s.incrementUnreadCount)

  const handleMessage = useCallback(
    (event: SSEEvent) => {
      incrementUnreadCount()
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      })

      const payload = event.data as Record<string, unknown> | undefined
      const notificationType = payload?.type as NotificationType | undefined
      if (notificationType) {
        invalidateForNotificationType(queryClient, notificationType, payload)
      }
    },
    [queryClient, incrementUnreadCount]
  )

  const handleReconnect = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.notifications.all,
    })
  }, [queryClient])

  useSSE({
    url: '/notifications/events',
    onMessage: handleMessage,
    onReconnect: handleReconnect,
    retry: true,
    retryInterval: 3000,
    autoDisconnectOnTerminal: false,
    openWhenHidden: true,
  })
}
